import styled, { css } from 'styled-components';
import { FindingsSeverity, ServiceOutcome } from '../../utils/services';

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 9998;
  background: rgba(18, 18, 18, 0.45);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
`;

export const Panel = styled.div<{ asModal?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  /* Nunca passar da altura disponível — no desktop quem limita é o Aside
     (sticky), no mobile é o Overlay (fixo). Em ambos os casos a rolagem do
     excedente fica no PanelBody. */
  max-height: 100%;
  min-height: 0;

  /* Vidro flutuante: translúcido sobre a imagem de fundo, sem exagerar. */
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px) saturate(115%);
  border: 1px solid rgba(255, 255, 255, 0.6);
  border-radius: var(--radius-lg);
  box-shadow: 0 10px 28px rgba(18, 18, 18, 0.16),
    0 2px 6px rgba(18, 18, 18, 0.06);

  ${props =>
    props.asModal &&
    css`
      max-width: 420px;
      background: rgba(255, 255, 255, 0.94);
      box-shadow: 0 18px 48px rgba(18, 18, 18, 0.28);
    `}
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid var(--border);

  h3 {
    margin: 0;
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--muted-foreground);
  }
`;

export const CloseButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: none;
  border: 1px solid var(--border);
  border-radius: var(--radius-full);
  padding: 0.25rem 0.7rem;
  font-family: var(--font-sans);
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--stone-600);

  &:hover {
    border-color: var(--stone-400);
    color: var(--foreground);
  }
`;

export const PanelBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  padding: 1rem;

  /* O cabeçalho do painel fica fixo e só o corpo rola. */
  min-height: 0;
  overflow-y: auto;
`;

export const Row = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  min-width: 0;
`;

export const RowLabel = styled.span`
  flex: 0 0 auto;
  font-size: 0.75rem;
  color: var(--muted-foreground);
`;

export const RowValue = styled.span<{ mono?: boolean }>`
  min-width: 0;
  text-align: right;
  font-size: 0.82rem;
  font-weight: 500;
  color: var(--foreground);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  ${props =>
    props.mono &&
    css`
      font-family: var(--font-mono);
    `}
`;

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--border);
  margin: 0.15rem 0;
`;

export const ServicesBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
`;

/* Cada serviço vira uma faixa colorida pelo que a consulta apurou nele, para
   o atendente varrer a lista e achar o problema sem ler item por item.

   `pending` é o estado antes de existir resultado — e é nele que a lista fica
   quando a flag "mostrar qual serviço apontou" está desligada, porque aí o
   backend não diz de qual serviço é cada resultado. Pintar tudo de verde nesse
   caso seria afirmar que está tudo limpo sem ter apurado.

   `failed` tem cor própria, separada de `clean`: consulta que falhou não é
   consulta sem apontamento. */
const outcomeColors: Record<
  ServiceOutcome,
  { bg: string; border: string; fg: string; icon: string }
> = {
  pending: {
    bg: 'transparent',
    border: 'transparent',
    fg: 'var(--stone-700)',
    icon: 'var(--primary-hover)',
  },
  findings: {
    bg: '#FDECEA',
    border: '#F3C9C5',
    fg: '#8A1F18',
    icon: 'var(--feedback-danger-strong)',
  },
  failed: {
    bg: '#FFF6DC',
    border: '#F2DFA6',
    fg: '#6B4A00',
    icon: 'var(--feedback-warning-strong)',
  },
  clean: {
    bg: '#E9F3EB',
    border: '#C9E2CE',
    fg: '#1B5326',
    icon: 'var(--feedback-success-strong)',
  },
};

interface OutcomeProps {
  outcome?: ServiceOutcome;
}

export const ServiceItem = styled.div<OutcomeProps>`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  font-size: 0.8rem;
  line-height: 1.35;

  /* Só ganha caixa quando há algo a comunicar — no estado neutro a lista
     continua com a aparência enxuta de antes.

     Os parâmetros vão anotados explicitamente (em vez de desestruturados)
     porque o styled-components não propaga o genérico para dentro da
     interpolação aqui — mesmo motivo do FindingsCount logo abaixo. */
  padding: ${(props: OutcomeProps) =>
    props.outcome && props.outcome !== 'pending' ? '0.4rem 0.55rem' : '0'};
  border: 1px solid
    ${(props: OutcomeProps) => outcomeColors[props.outcome ?? 'pending'].border};
  border-radius: var(--radius-md);
  background: ${(props: OutcomeProps) =>
    outcomeColors[props.outcome ?? 'pending'].bg};
  color: ${(props: OutcomeProps) =>
    outcomeColors[props.outcome ?? 'pending'].fg};

  svg {
    flex: 0 0 auto;
    margin-top: 0.2rem;
    color: ${(props: OutcomeProps) =>
      outcomeColors[props.outcome ?? 'pending'].icon};
  }
`;

/** Nome do serviço, para o contador poder ficar encostado à direita. */
export const ServiceName = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

/* Marca que a empresa não filtrou nada e a esteira inteira vai rodar — a
   lista continua aparecendo item a item, isso é só o rótulo do conjunto. */
export const AllServicesTag = styled.span`
  font-size: 0.72rem;
  font-style: italic;
  color: var(--muted-foreground);
`;

/* Quantidade de apontamentos do serviço, com o fundo colorido pela faixa de
   severidade (mesma rampa de intensidade da página de Score). */
const severityBackground: Record<FindingsSeverity, string> = {
  none: 'var(--stone-400)',
  low: '#ca8a04',
  medium: '#ea580c',
  high: '#dc2626',
};

export const FindingsCount = styled.span<{ severity: FindingsSeverity }>`
  flex: 0 0 auto;
  min-width: 1.25rem;
  padding: 0.05rem 0.35rem;
  border-radius: var(--radius-full);
  font-size: 0.7rem;
  font-weight: 700;
  line-height: 1.5;
  text-align: center;
  color: #fff;
  background: ${(props: { severity: FindingsSeverity }) =>
    severityBackground[props.severity]};
`;
